import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="tip-rotation"
export default class extends Controller {
    static identifier = "tip-rotation"
    static values = {
        speed: { type: Number, default: 50 },
        delay: { type: Number, default: 10000 },
        tips: Array,
        maxLength: Number
    }

    initialize() {
        this.tipIndex = 0
        this.nextTipTimeout = null
        this.frame = null
        this.tipSpan = null
    }

    connect() {
        if (!this.hasTips()) return

        this.setupElements()
        this.showNextTip()
    }

    disconnect() {
        this.clearNextTipTimeout()
        this.frame = null
        this.tipSpan = null
    }

    showNextTip() {
        if (!this.hasTips()) return

        const tip = this.tipsValue[this.tipIndex]
        const displayDuration = this.renderTip(tip)

        this.tipIndex = (this.tipIndex + 1) % this.tipsValue.length
        this.scheduleNextTip(displayDuration)
    }

    scheduleNextTip(duration = this.delayValue) {
        this.clearNextTipTimeout()
        this.nextTipTimeout = setTimeout(() => this.showNextTip(), duration)
    }

    clearNextTipTimeout() {
        if (this.nextTipTimeout) {
            clearTimeout(this.nextTipTimeout)
            this.nextTipTimeout = null
        }
    }

    hasTips() {
        return Array.isArray(this.tipsValue) && this.tipsValue.length > 0
    }

    setupElements() {
        this.ensureElements()
        this.applyMaxWidth()
    }

    applyMaxWidth() {
        const maxLength = this.maxLengthValue || this.maxTipLength()
        if (maxLength > 0) {
            this.element.style.setProperty("--tip-max-ch", maxLength + 1)
        }
    }

    maxTipLength() {
        let maxLength = 0
        for (const tip of this.tipsValue || []) {
            const length = Array.from(String(tip)).length
            if (length > maxLength) maxLength = length
        }
        return maxLength
    }

    ensureElements() {
        if (this.frame && this.tipSpan) {
            this.tipSpan.textContent = ""
            return
        }

        this.frame = this.element.querySelector(".tip-frame")
        if (!this.frame) {
            this.frame = document.createElement("span")
            this.frame.className = "tip-frame"
            this.element.textContent = ""
            this.element.append(this.frame)
        }

        this.tipSpan = document.createElement("span")
        this.tipSpan.className = "tip-text text-accent"

        this.frame.textContent = ""
        this.frame.append(this.tipSpan)
    }

    renderTip(text) {
        this.ensureElements()

        this.resetTipClasses()

        const tipText = String(text)
        const charCount = Math.max(Array.from(tipText).length, 1)
        const revealDuration = charCount * this.typingSpeed()

        this.tipSpan.textContent = tipText
        this.setVars({
            "--tip-steps": charCount,
            "--tip-chars": charCount,
            "--tip-duration": `${revealDuration}ms`
        })

        // Force a reflow so the typing animation restarts cleanly.
        void this.tipSpan.offsetWidth

        const { frameWidth, textWidth } = this.measureWidths()

        if (frameWidth > 0) {
            const scrollDuration = this.setScrollVars(frameWidth, textWidth)
            this.applyScrollClasses()
            return Math.max(this.delayValue, scrollDuration)
        }

        this.clearScrollVars()
        this.applyTypingClasses()
        return this.delayValue
    }

    typingSpeed() {
        return Math.max(1, this.speedValue)
    }

    resetTipClasses() {
        this.tipSpan.classList.remove("is-typing", "is-scrolling")
    }

    applyTypingClasses() {
        this.tipSpan.classList.add("is-typing")
    }

    applyScrollClasses() {
        this.tipSpan.classList.add("is-scrolling")
    }

    measureWidths() {
        return {
            frameWidth: this.frame?.clientWidth || this.element.clientWidth,
            textWidth: this.tipSpan.scrollWidth
        }
    }

    setScrollVars(frameWidth, textWidth) {
        // For short text that fits in the frame, slide in from right and end at right edge
        // For long text, slide in from right and scroll completely off to the left
        const scrollEnd = textWidth > frameWidth + 1 
            ? -textWidth  // Long text: scroll completely off to the left
            : frameWidth - textWidth  // Short text: end at right edge, fully visible
        
        const totalDistance = Math.abs(frameWidth - scrollEnd)
        const scrollDuration = Math.max(totalDistance * 18, 6000)

        this.setVars({
            "--tip-scroll-start": `${frameWidth}px`,
            "--tip-scroll-end": `${scrollEnd}px`,
            "--tip-scroll-duration": `${scrollDuration}ms`
        })

        return scrollDuration
    }

    clearScrollVars() {
        this.setVars({
            "--tip-scroll-start": "0px",
            "--tip-scroll-end": "0px",
            "--tip-scroll-duration": "0ms"
        })
    }

    setVars(vars) {
        Object.entries(vars).forEach(([key, value]) => {
            this.element.style.setProperty(key, String(value))
        })
    }
}
